import React, { useEffect, useState, useRef, CSSProperties } from 'react';
import { Select, Button, Row, Col, Spin, DatePicker } from 'antd';
import styled from 'styled-components';
import { restApi } from '../../../constants/api';
import { PrinterOutlined, RedoOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;

const FilterContainer = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
`;

const headerCellStyle: CSSProperties = {
  padding: '12px',
  textAlign: 'center',
  border: '1px solid #ddd',
};

const bodyCellStyle: CSSProperties = {
  padding: '12px',
  border: '1px solid #ddd',
};

const months = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const currentYear = dayjs().year();
const years = Array.from({ length: 5 }, (_, idx) => currentYear - idx);

const Salary = () => {
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [employeeData, setEmployeeData] = useState([]);
  const [Messagetext, setMessageText] = useState<string>('');
  const [loading, setLoading] = useState(true);

  // 🔹 FIX: start as false so button is NOT loading initially
  const [searchingloading, setSearchingloading] = useState(false);

  const printRef: any = useRef();

  const [selected, setSelected] = useState({
    department: undefined as number | undefined,
    user: undefined as number | undefined,
    designation: undefined as number | undefined,
    startDate: undefined as string | undefined,
    endDate: undefined as string | undefined,
    month: undefined as number | undefined,
    year: currentYear as number | undefined,
  });

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [deptRes, userRes, desigRes]: any = await Promise.all([
          restApi('/api/departments', 'GET'),
          restApi('/api/users/dropdown', 'GET'),
          restApi('/api/user-types', 'GET'),
        ]);
        setDepartments(deptRes?.data || []);
        setUsers(userRes?.data || []);
        setDesignations(desigRes?.data || []);
      } catch (error) {
        console.error('Error fetching filters:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFilters();
  }, []);

  const handleSearch = async () => {
    setMessageText('');

    // 🔹 Validate FIRST, before turning loading on
    if (!selected.month) {
      setMessageText('Please select a month.');
      return;
    }

    setSearchingloading(true);

    try {
      let start_date, end_date;

      const year = selected.year || currentYear;
      const start = dayjs(`${year}-${selected.month}-01`);
      start_date = start.format('YYYY-MM-DD');
      end_date = start.endOf('month').format('YYYY-MM-DD');

      const body = {
        start_date,
        end_date,
        user_id: selected.user,
        user_type_id: selected.designation,
        department_id: selected.department,
      };

      const res: any = await restApi('/api/reports/salary', 'POST', body);
      if (!res.data || res.data.length === 0) {
        setMessageText('No data was found for the selected month.');
        setEmployeeData([]);
      } else {
        setEmployeeData(res.data);
      }
    } catch (error) {
      console.error('Error fetching employee details:', error);
      setMessageText('Something went wrong while fetching data.');
    } finally {
      // 🔹 ALWAYS stop loading
      setSearchingloading(false);
    }
  };

  const handleReset = () => {
    setMessageText('');
    setSelected({
      department: undefined,
      user: undefined,
      designation: undefined,
      startDate: undefined,
      endDate: undefined,
      month: undefined,
      year: currentYear,
    });
    setEmployeeData([]);
    // 🔹 Ensure button not stuck in loading after reset
    setSearchingloading(false);
  };

  const handlePrint = () => {
    const printContent: any = printRef.current;
    const newWin: any = window.open('', '', 'width=1000,height=800');
    newWin.document.write('<html><head><title>Print</title>');
    newWin.document.write(
      '<style>table, th, td { border: 1px solid black; border-collapse: collapse; padding: 4px; font-size: 12px; }</style>'
    );
    newWin.document.write('</head><body>');
    newWin.document.write(printContent.innerHTML);
    newWin.document.write('</body></html>');
    newWin.document.close();
    newWin.print();
  };

  const groupByDepartment = (data: any[]) => {
    const grouped: any = {};
    data.forEach((emp) => {
      if (!grouped[emp.department_name]) {
        grouped[emp.department_name] = [];
      }
      grouped[emp.department_name].push(emp);
    });
    return grouped;
  };

  const groupedData = groupByDepartment(employeeData);

  const selectedMonthLabel =
    months.find((m) => m.value === selected.month)?.label || '';
  const selectedYearLabel = selected.year || currentYear;

  return (
    <>
      <FilterContainer>
        {loading ? (
          <Spin size="large" />
        ) : (
          <Row gutter={[16, 16]}>
            {localStorage.getItem('role') === 'SUPERADMIN' && (
              <Col xs={24} md={6}>
                <Select
                  placeholder="Select Department"
                  value={selected.department}
                  onChange={(value) =>
                    setSelected((prev) => ({ ...prev, department: value }))
                  }
                  style={{ width: '100%' }}
                  allowClear
                >
                  {departments.map((d: any) => (
                    <Option key={d.id} value={d.id}>
                      {d.name}
                    </Option>
                  ))}
                </Select>
              </Col>
            )}

            <Col xs={24} md={8}>
              <Select
                placeholder="Select Designation"
                value={selected.designation}
                onChange={(value) =>
                  setSelected((prev) => ({ ...prev, designation: value }))
                }
                style={{ width: '100%' }}
                allowClear
              >
                {designations.map((d: any) => (
                  <Option key={d.id} value={d.id}>
                    {d.type_name}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} md={6}>
              <Select
                placeholder="Select User"
                value={selected.user}
                onChange={(value) =>
                  setSelected((prev) => ({ ...prev, user: value }))
                }
                style={{ width: '100%' }}
                allowClear
              >
                {users.map((u: any) => (
                  <Option key={u.id} value={u.id}>
                    {u.name}
                  </Option>
                ))}
              </Select>
            </Col>

            {/* Month + Year */}
            <Col xs={24} md={4}>
              <div style={{ display: 'flex', gap: 8 }}>
                <Select
                  placeholder="Month"
                  value={selected.month}
                  onChange={(value) =>
                    setSelected((prev) => ({ ...prev, month: value }))
                  }
                  style={{ width: '100%' }}
                  allowClear
                >
                  {months.map((month) => (
                    <Option key={month.value} value={month.value}>
                      {month.label}
                    </Option>
                  ))}
                </Select>

                <Select
                  placeholder="Year"
                  value={selected.year}
                  onChange={(value) =>
                    setSelected((prev) => ({ ...prev, year: value }))
                  }
                  style={{ width: '100%' }}
                  allowClear={false}
                >
                  {years.map((year) => (
                    <Option key={year} value={year}>
                      {year}
                    </Option>
                  ))}
                </Select>
              </div>
            </Col>

            <Col xs={24} style={{ textAlign: 'right' }}>
              <Button
                style={{ background: '#77d6c9', color: 'black' }}
                icon={<SearchOutlined style={{ color: 'white', fontSize: '17px' }} />}
                onClick={handleSearch}
                loading={searchingloading}
              >
                Search
              </Button>{' '}
              &nbsp;
              <Button
                style={{ background: '#77d6c9', color: 'black' }}
                icon={<RedoOutlined style={{ color: 'white', fontSize: '17px' }} />}
                onClick={handleReset}
              >
                Reset
              </Button>
            </Col>
          </Row>
        )}
      </FilterContainer>

      {Messagetext && (
        <p
          style={{
            color: 'red',
            marginTop: 16,
            textAlign: 'center',
            fontSize: '22px',
          }}
        >
          {Messagetext}
        </p>
      )}

      {employeeData.length > 0 && (
        <>
          <div style={{ margin: '20px 0', textAlign: 'right' }}>
            <Button
              style={{ background: '#77d6c9', color: 'black' }}
              icon={<PrinterOutlined style={{ color: 'white', fontSize: '17px' }} />}
              onClick={handlePrint}
            >
              Print
            </Button>
          </div>

          <div ref={printRef} style={{ background: 'white', padding: 16 }}>
            <>
              <h3
                style={{
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  marginBottom: 20,
                }}
              >
                Details of Monthly Salaries for Employees at Zilha Sainik Welfare
                Office, Kolhapur
                <br />
                ({selectedMonthLabel} {selectedYearLabel})
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#76d5c8', color: '#000' }}>
                  <tr>
                    <th style={headerCellStyle}>SR NO</th>
                    <th style={headerCellStyle}>MR/MRS</th>
                    <th style={headerCellStyle}>NAME</th>
                    <th style={headerCellStyle}>Mobile No</th>
                    <th style={headerCellStyle}>DESIGNATION</th>
                    <th style={headerCellStyle}>Adharnumber</th>
                    <th style={headerCellStyle}>PAN No</th>
                    <th style={headerCellStyle}>A/C No</th>
                    <th style={headerCellStyle}>Bank Name</th>
                    <th style={headerCellStyle}>Branch IFSC Code</th>
                    <th style={headerCellStyle}>Payment (1 Month)</th>
                    <th style={headerCellStyle}>Total Days Cover</th>
                    <th style={headerCellStyle}>Absent Days</th>
                    <th style={headerCellStyle}>Payment Of Month</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedData).map(
                    ([department, emps]: any, deptIndex) => (
                      <React.Fragment key={`dept-block-${deptIndex}`}>
                        <tr style={{ backgroundColor: '#f5f5f5' }}>
                          <td
                            colSpan={14}
                            style={{
                              fontWeight: 'bold',
                              padding: '12px',
                              textAlign: 'center',
                              border: '1px solid #ddd',
                            }}
                          >
                            {department}
                          </td>
                        </tr>
                        {emps.map((emp: any, index: number) => (
                          <tr
                            key={`emp-${deptIndex}-${index}`}
                            style={{ textAlign: 'center', cursor: 'pointer' }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.backgroundColor = '#f0f0f0')
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.backgroundColor = '')
                            }
                          >
                            <td style={bodyCellStyle}>{index + 1}</td>
                            <td style={bodyCellStyle}>{emp.prefixName}</td>
                            <td style={bodyCellStyle}>{emp.user_name || ''}</td>
                            <td style={bodyCellStyle}>{emp.mobileNo || ''}</td>
                            <td style={bodyCellStyle}>{emp.designation || ''}</td>
                            <td style={bodyCellStyle}>{emp.adharnumber || ''}</td>
                            <td style={bodyCellStyle}>{emp.pannumber || ''}</td>
                            <td style={bodyCellStyle}>{emp.accountNumber || ''}</td>
                            <td style={bodyCellStyle}>{emp.bankName || ''}</td>
                            <td style={bodyCellStyle}>{emp.branchIfscCode || ''}</td>
                            <td style={bodyCellStyle}>{emp.perMonthSalary || ''}</td>
                            <td style={bodyCellStyle}>
                              {emp.days_covered_in_month || ''}
                            </td>
                            <td style={bodyCellStyle}>{emp.absent_days || ''}</td>
                            <td style={bodyCellStyle}>
                              {emp.calculated_salary || ''}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    )
                  )}
                </tbody>
              </table>
            </>
          </div>
        </>
      )}
    </>
  );
};

export default Salary;
